import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { Icon, type ProjectDetailHeaderMetaItem } from '@verantos-dev/shared-components';

import type { UseAnalysisDetailHeaderMetaArgs, UseAnalysisDetailHeaderMetaResult } from '../types';
import { toAssetStatus } from '../utils/assetStatusLabel';

// Type-caption icon for the condensed meta row. Sized to sit alongside the
// shared `Icon` glyphs (owner) which render at 16px.
const HEADER_ICON_SX = { fontSize: 18, color: 'text.secondary' } as const;

// Derives the analysis detail header's status + meta row (type / owner /
// last-modified) from the raw profile fields. Isolated from route/tab/fetch
// orchestration (`useAnalysisDetailPage`) so this composition can be tested
// without mocking the whole page hook.
export const useAnalysisDetailHeaderMeta = ({
  latestStatus,
  isMetaReady,
  ownerName,
  lastModifiedDate,
}: UseAnalysisDetailHeaderMetaArgs): UseAnalysisDetailHeaderMetaResult => {
  const { formatMessage, formatDate, formatTime } = useIntl();
  const theme = useTheme();

  const effectiveStatus = latestStatus ? toAssetStatus(latestStatus) : undefined;

  // Mirror the cohort/AV/AD header's `date | time` format.
  const lastModified = lastModifiedDate
    ? `${formatDate(lastModifiedDate, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })} | ${formatTime(lastModifiedDate, { hour: 'numeric', minute: '2-digit' })}`
    : null;

  const metaItems = (
    [
      {
        key: 'type',
        icon: <AnalyticsOutlinedIcon sx={HEADER_ICON_SX} />,
        label: formatMessage({ id: 'analysisDetail.meta.typeLabel' }),
        value: formatMessage({ id: 'analysisDetail.meta.typeValue' }),
      },
      ownerName
        ? {
            key: 'owner',
            icon: (
              <Icon
                id="analysis-detail-owner-icon"
                icon="owner"
                width={16}
                height={16}
                sx={{ fill: theme.palette.text.secondary }}
              />
            ),
            label: formatMessage({ id: 'analysisDetail.meta.ownerLabel' }),
            value: ownerName,
          }
        : null,
      lastModified
        ? {
            key: 'lastModified',
            icon: (
              <AccessTimeOutlinedIcon
                data-testid="analysis-detail-last-modified-icon"
                sx={HEADER_ICON_SX}
              />
            ),
            label: formatMessage({ id: 'analysisDetail.meta.lastModifiedLabel' }),
            value: lastModified,
          }
        : null,
    ] as (ProjectDetailHeaderMetaItem | null)[]
  ).filter((item): item is ProjectDetailHeaderMetaItem => item !== null);

  return {
    effectiveStatus,
    metaItems: isMetaReady ? metaItems : undefined,
  };
};
